import { CustomerHandoffDialog } from '@/components/forms/Refunds/CustomerHandoffDialog'
import { RefundDestinationList } from '@/components/forms/Refunds/RefundDestinationList'
import {
  getRefundDestinationPreviewLines,
  getRefundDestinationTotals,
} from '@/components/forms/Refunds/refund-destinations'
import { CustomerNotificationSwitch } from '@/components/orders/CustomerNotificationSwitch'
import { InfoNotice } from '@/components/patterns/info-notice'
import { PosCartTotalRow } from '@/components/pos/PosCartTotalRow'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import { getOrderQuoteLockState, orderChangePaymentLockReason } from '@/lib/order-quote'
import { getOrderChangeRows } from '@/lib/pos/order-change'
import { canManageSalesOrderField } from '@/lib/sales-order-access'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { getPosCart } from '@/lib/services/carts/carts.api'
import { cartQueryKeys, useIsCartMutating, useIsCartSaving } from '@/lib/services/carts/carts.query'
import type { PosCart } from '@/lib/services/carts/carts.types'
import { manualTenderLocationId } from '@/lib/services/cash-drawers/cash-drawers.utils'
import { useRefundCashDrawer } from '@/lib/services/cash-drawers/useRefundCashDrawer'
import { isPendingRefund, isUnsettledRefund } from '@/lib/services/refunds/refunds.utils'
import {
  refreshSavedOrderChange,
  useApplySalesOrderChangeMutation,
} from '@/lib/services/sales-orders/sales-order-changes.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useRestartOrderChange } from './DiscardOrderChangeButton'
import { OrderChangeCancelAction } from './OrderChangeCancelAction'
import { PayLaterConfirmationDialog } from './PayLaterConfirmationDialog'
import { PaymentModal } from './payment-modal-loader'

type PosOrderChangeSummaryProps = {
  cart: PosCart
  /** Told when the change's Pay Now opens or closes, so the edit screen keeps the change's completion behind it. */
  onPayNowOpenChange?: ((open: boolean) => void) | undefined
}

/**
 * What saving this edit cart does to its order, and the actions that save it: what the change adds is collected on
 * the order's own Pay Now, whose payment saves the change; a balance can be left on the customer's account instead;
 * payment a lower total no longer needs goes back with the save, through the refund flow; a change that moves no
 * money saves on its own. Taking every item off is cancelling the order.
 */
export function PosOrderChangeSummary({ cart, onPayNowOpenChange }: PosOrderChangeSummaryProps) {
  const navigate = useNavigate()
  const path = usePathHelper()
  const queryClient = useQueryClient()
  const ability = useAbility()
  const isCartMutating = useIsCartMutating()
  const isRepricing = useIsCartSaving() > 0
  const applyChange = useApplySalesOrderChangeMutation()
  const change = cart.order_change ?? null
  const restart = useRestartOrderChange(change?.sales_order_id ?? 0, cart.id)
  const quote = change?.quote ?? null
  const summary = quote?.summary ?? null
  // Payment the new total no longer needs; the save returns it through the refund flow, to the payments the
  // refund planner names.
  const refund = quote?.refund ?? null
  // What the change adds is collected here, through the order's own Pay Now; the payment saves the change.
  const [paymentModalMode, setPaymentModalMode] = useState<'split' | null>(null)
  const setPayNowMode = (mode: 'split' | null) => {
    setPaymentModalMode(mode)
    onPayNowOpenChange?.(mode !== null)
  }
  const [isConfirmingHandoff, setIsConfirmingHandoff] = useState(false)
  // As at the POS, a balance left for later is confirmed against the customer's account first.
  const [isConfirmingPayLater, setIsConfirmingPayLater] = useState(false)
  // Set before the till is chosen, so a second click can't start the save again while it is.
  const [isSaving, setIsSaving] = useState(false)
  // The email goes to the customer the draft names, so the choice follows the draft's customer.
  const canEmailCustomer = Boolean(cart.purchaser?.email)
  const [wantsCustomerEmail, setNotifyCustomer] = useState(true)
  const notifyCustomer = canEmailCustomer && wantsCustomerEmail
  const { hasChanges } = getOrderChangeRows(cart)
  const difference = parseCurrency(summary?.difference ?? 0)
  const balanceDue = parseCurrency(summary?.balance_due_after ?? 0)
  const refundTotal = parseCurrency(refund?.total ?? 0)
  // Collecting is offered for what the change adds; a draft that adds nothing just saves.
  const offersCollection = difference > 0 && balanceDue > 0
  // Details alone (items' notes, recipe or description; the order's customer, fulfillment, recipient or personal
  // touches) change nothing the customer pays, so no email goes out for them.
  const changesOnlyDetails = Boolean(
    (quote?.details_changed || quote?.order_details_changed) && !quote.changes_document && !refund
  )
  // The save and a payment toward the draft ask for the email only when the screen offers it.
  const sendsCustomerEmail = notifyCustomer && !changesOnlyDetails
  const destinationLines = getRefundDestinationPreviewLines(refund?.refund_plan)
  const { manualDestinationTotal, cardDestinationTotal } = getRefundDestinationTotals(
    refund?.refund_plan
  )
  // Cash or a check handed back is confirmed, and comes out of a till when Cash Drawer tracking is on.
  const needsCashConfirmation = manualDestinationTotal > 0
  const isBusy = isSaving || isCartMutating > 0
  // The server enforces these too; saying why up front saves a refused save.
  const blockedReason =
    summary?.blocked_reason ??
    (refund && !refund.eligible
      ? (refund.ineligible_reason ??
        'This refund can’t go back to the payments on this order. Refund the order instead.')
      : difference !== 0 && !canManageSalesOrderField(ability, 'payments')
        ? 'Changing the order total needs payment access. Ask a manager to save this change.'
        : refund && !canManageSalesOrderField(ability, 'refunds')
          ? 'Refunding the customer needs refund access. Ask a manager to save this change.'
          : null)
  // As at the POS, leaving a balance for later is Pay Later; the server says why it isn't available, in its own words.
  const payLaterUnavailableReason = summary?.pay_later_unavailable_reason ?? null
  const { resolve: resolveRefundCashDrawer } = useRefundCashDrawer({
    organizationId: cart.organization_id,
    locationId: manualTenderLocationId(cart),
    canOperateCashDrawers: ability.can('operate', 'cash-drawers'),
    enabled: needsCashConfirmation,
  })
  const canSave = Boolean(summary) && hasChanges && !isBusy && !blockedReason
  // A payment toward the draft holds it as priced (the cart's lock says so): only its Pay Now (to check or cancel it) acts.
  const paymentInProgress = getOrderQuoteLockState(cart).lockReason === orderChangePaymentLockReason
  const salesOrderId = change?.sales_order_id ?? null
  // The order as the edit route loaded it: a balance left for later keeps the due date the order was placed with.
  const { data: order } = useQuery({
    ...salesOrderQueries.show({
      organizationId: cart.organization_id,
      salesOrderId: salesOrderId ?? 0,
    }),
    enabled: salesOrderId !== null,
  })
  // Pay Now reads the order as the open draft leaves it; once the draft carries no snapshot (its change saved, or it is
  // out of date while a card or reader may still be pending), the order's own: Add Another Payment pays the order.
  const openPaymentSnapshot =
    change?.payment_snapshot ??
    (order && 'payment_snapshot' in order ? order.payment_snapshot : null)

  // The saved draft is closed: the editor leaves it before anything reads again, so the screen never shows it closed
  // on the way out, and Back from the order page must not return to it. Pay Now is done by then, though its blocker
  // (a cash change just acknowledged) may not have unmounted yet.
  const showSavedOrder = async () => {
    if (salesOrderId === null) return
    await navigate({
      ...path.salesOrders.show(salesOrderId).link,
      replace: true,
      ignoreBlocker: true,
    })
    void refreshSavedOrderChange(cart.organization_id, cart.id)
  }

  // The till cash comes back from is checked before staff are asked to hand it back, and again as the save submits.
  const startHandoff = async () => {
    try {
      await resolveRefundCashDrawer()
      setIsConfirmingHandoff(true)
    } catch (error) {
      toast.error('Unable to save this change', { description: getErrorMessage(error) })
    }
  }

  /**
   * Saves without collecting: a balance left for later, a change that adds nothing to collect, or one that returns
   * the payment it no longer needs. A repeat answers with the same save.
   */
  const save = async ({ payLater }: { payLater: boolean }) => {
    if (salesOrderId === null || !quote) return
    setIsSaving(true)
    try {
      const cashDrawerSessionId = needsCashConfirmation ? await resolveRefundCashDrawer() : null
      const result = await applyChange.mutateAsync({
        salesOrderId,
        cart_id: cart.id,
        expected_difference: difference,
        expected_refund: refundTotal,
        expected_version: quote.version,
        send_customer_email: sendsCustomerEmail,
        pay_later: payLater,
        cash_handoff_confirmed: needsCashConfirmation,
        cash_drawer_session_id: cashDrawerSessionId,
      })
      if (refundTotal > 0 && (!result.refund_status || isUnsettledRefund(result.refund_status))) {
        // The change saved; the order page offers the return again.
        toast.warning('Order updated, but the refund didn’t go through', {
          description: 'Review the refund on the order.',
        })
      } else if (refundTotal > 0 && isPendingRefund(result.refund_status)) {
        // Its money is still on its way; the order page follows the refund to settlement.
        toast.success('Order updated', {
          description: `The ${formatCurrency(refundTotal)} refund is processing.`,
        })
      } else if (refundTotal > 0) {
        toast.success('Order updated', { description: `${formatCurrency(refundTotal)} refunded` })
      } else {
        toast.success('Order updated')
      }
      await showSavedOrder()
    } catch (error) {
      toast.error('Unable to save this change', { description: getErrorMessage(error) })
      // Show the draft as it is now: it may have been edited, or the order refunded, while this saved.
      await queryClient.invalidateQueries({
        queryKey: cartQueryKeys.all({ organizationId: cart.organization_id }),
      })
    } finally {
      setIsSaving(false)
      setIsConfirmingHandoff(false)
    }
  }

  /**
   * Pay Now collected what the change adds, and the server saved the change with the payment, unless the draft could
   * no longer apply when the money posted (stock sold since, the order refunded): the payment then posts to the order
   * and the draft stays open, so it is read once more before the editor leaves. Only a draft that reads back saved
   * (or is gone) proves the save; one discarded meanwhile, or a read that fails any other way, does not. The read is
   * its own: the draft this screen holds may not have caught up with the payment's answer yet.
   */
  const finishCollected = async () => {
    const draft: 'open' | 'saved' | 'discarded' | 'unread' = await getPosCart({
      queryKey: cartQueryKeys.posShow({ organizationId: cart.organization_id, cartId: cart.id }),
    }).then(
      (read) => read.order_change?.closed ?? 'open',
      (error: unknown) =>
        isAxiosError(error) && error.response?.status === 404 ? 'saved' : 'unread'
    )
    if (draft !== 'saved') {
      toast.warning(
        draft === 'unread'
          ? 'Payment taken, but we couldn’t confirm the change saved'
          : 'Payment taken, but the change did not save',
        {
          description:
            draft === 'open'
              ? 'The order holds the payment. Check the change below and save it again.'
              : draft === 'discarded'
                ? 'The order holds the payment. Start the change again from the order.'
                : 'Check the order before leaving this screen.',
        }
      )
      await queryClient.invalidateQueries({
        queryKey: cartQueryKeys.posShow({ organizationId: cart.organization_id, cartId: cart.id }),
      })
      return
    }
    // Pay Now already showed what was taken, which may be less than the balance when the order owed money before.
    toast.success('Order updated')
    await showSavedOrder()
  }

  // The order's Pay Now, for what the change adds; the server saves the change as the payment posts.
  const paymentDialog =
    paymentModalMode && openPaymentSnapshot && salesOrderId !== null ? (
      <PaymentModal
        mode={paymentModalMode}
        onModeChange={(mode) => {
          // Its change saved: closing Pay Now, after a last payment or not, leaves for the order. The edit screen still
          // counts Pay Now open, so the change's completion doesn't flash on the way out.
          if (mode === null && change?.closed === 'saved') {
            setPaymentModalMode(null)
            toast.success('Order updated')
            void showSavedOrder()
            return
          }
          setPayNowMode(mode)
          // The draft's lock is the server's: closing Pay Now reads the draft again, whatever the payment did.
          if (mode === null) {
            void queryClient.invalidateQueries({
              queryKey: cartQueryKeys.posShow({
                organizationId: cart.organization_id,
                cartId: cart.id,
              }),
            })
          }
        }}
        paymentSnapshot={openPaymentSnapshot}
        paymentBootstrap={openPaymentSnapshot.payment_bootstrap}
        paymentTarget={{
          kind: 'sales_order',
          salesOrderId,
          cartId: null,
          customerId: cart.purchaser?.id ?? null,
          orderChangeCartId: cart.id,
          orderChangeSendCustomerEmail: sendsCustomerEmail,
        }}
        paymentConfiguration={{ title: 'Collect Payment' }}
        // A draft already read saved left for the order as Pay Now closed; any other is read to prove the save.
        onSuccessfulClose={() => {
          if (change?.closed !== 'saved') void finishCollected()
        }}
      />
    ) : null

  // Pay Now keeps one place in every state below: a draft that saves or goes out of date under it must not remount it,
  // which would drop the payment it is finishing.
  const withPaymentDialog = (content: ReactNode) => (
    <>
      {content}
      {paymentDialog}
    </>
  )

  // A closed change has nothing left to save; its completion shows over the draft. A Pay Now open on it stays: the
  // payment that saved the change leaves it open for Add Another Payment.
  if (change?.closed) return withPaymentDialog(null)

  if (change?.available_reason) {
    return withPaymentDialog(
      <InfoNotice
        tone="warning"
        title="This order can’t be changed"
        body={change.available_reason}
      />
    )
  }

  // The order changed, or was refunded, under this draft: it prices nothing stale, so the change starts again.
  if (change?.restart_reason) {
    return withPaymentDialog(
      <InfoNotice
        tone="warning"
        title="This change is out of date"
        body={change.restart_reason}
        action={
          <Button
            variant="notice"
            size="xs"
            onClick={restart.start}
            disabled={restart.isPending || isCartMutating > 0 || paymentInProgress}
          >
            {restart.isPending ? 'Starting over…' : 'Start over'}
          </Button>
        }
      />
    )
  }

  if (!summary || !change || salesOrderId === null) {
    return withPaymentDialog(
      <InfoNotice
        tone="warning"
        title="We couldn’t price this change"
        body={change?.quote_error ?? 'Check the items and try again.'}
      />
    )
  }

  // Taking every item off is cancelling the order, through its own cancel flow, which returns what was paid.
  const cancelsOrder = summary.cancels_order
  // A change settles in itself: it collects what it adds or returns what it no longer needs, never both.
  const primaryLabel = offersCollection
    ? `Collect ${formatCurrency(balanceDue)} and save`
    : refundTotal > 0
      ? `Save and refund ${formatCurrency(refundTotal)}`
      : 'Save changes'
  const paymentSnapshot = change.payment_snapshot ?? null

  return withPaymentDialog(
    <div className="space-y-3 border-t pt-3" data-testid="pos-order-change-summary">
      <div className="grid grid-cols-1 gap-y-1">
        <PosCartTotalRow label="Original total" value={formatCurrency(summary.original_total)} />
        {cancelsOrder ? null : (
          <>
            <PosCartTotalRow
              label="New total"
              value={formatCurrency(summary.new_total)}
              pending={isRepricing}
            />
            <PosCartTotalRow
              label="Difference"
              strong
              pending={isRepricing}
              value={`${difference > 0 ? '+' : ''}${formatCurrency(difference)}`}
            />
          </>
        )}
      </div>
      {cancelsOrder ? (
        <OrderChangeCancelAction salesOrderId={salesOrderId} disabled={isBusy} />
      ) : (
        <>
          {refund ? (
            <div className="space-y-1">
              <PosCartTotalRow
                label="Refund to customer"
                value={formatCurrency(refundTotal)}
                pending={isRepricing}
              />
              <RefundDestinationList lines={destinationLines} />
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-y-1">
            <PosCartTotalRow label="Paid so far" value={formatCurrency(summary.collected)} />
            <PosCartTotalRow
              label={balanceDue > 0 ? 'Balance due' : 'Nothing owed'}
              strong
              pending={isRepricing}
              value={formatCurrency(balanceDue)}
            />
          </div>
          {changesOnlyDetails ? null : canEmailCustomer ? (
            <CustomerNotificationSwitch
              notifyCustomer={notifyCustomer}
              onNotifyCustomerChange={setNotifyCustomer}
              disabled={isBusy}
            />
          ) : (
            <Typography variant="description">
              No customer email address is saved. No email will be sent.
            </Typography>
          )}

          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="success"
              disabled={!canSave || (offersCollection ? !paymentSnapshot : paymentInProgress)}
              onClick={() =>
                offersCollection
                  ? setPayNowMode('split')
                  : needsCashConfirmation
                    ? void startHandoff()
                    : void save({ payLater: false })
              }
            >
              {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
              {hasChanges ? primaryLabel : 'No changes yet'}
            </Button>
            {/* As at the POS: collecting now is open to anyone; leaving the balance is Pay Later, under its rules. */}
            {offersCollection ? (
              <PayLaterConfirmationDialog
                title="Save and collect later?"
                description="Review what will be left unpaid before saving the change."
                customer={cart.purchaser}
                unpaidAmount={balanceDue}
                projection={summary}
                orderDueDate={order ? order.due_date : undefined}
                onConfirm={() => save({ payLater: true })}
                onOpenChange={setIsConfirmingPayLater}
                open={isConfirmingPayLater}
                isPending={isSaving}
                pendingText="Saving..."
                confirmText="Save Change"
                trigger={
                  <Button
                    variant="outline"
                    disabled={!canSave || paymentInProgress || Boolean(payLaterUnavailableReason)}
                  >
                    Save, collect later
                  </Button>
                }
              />
            ) : null}
            {offersCollection && payLaterUnavailableReason ? (
              <Typography variant="supporting" as="p">
                {payLaterUnavailableReason}
              </Typography>
            ) : null}
            {/* Why saving is unavailable, under the actions as the cart's own payment blockers are. */}
            {blockedReason ? (
              <Typography variant="label" tone="muted">
                {blockedReason}
              </Typography>
            ) : null}
            {paymentInProgress ? (
              <Typography variant="label" tone="muted">
                A payment toward this change is still in progress.
              </Typography>
            ) : null}
          </div>
          <CustomerHandoffDialog
            open={isConfirmingHandoff}
            onOpenChange={setIsConfirmingHandoff}
            manualTotal={manualDestinationTotal}
            electronicTotal={cardDestinationTotal}
            confirmLabel="Confirm Handoff & Save"
            onConfirm={() => void save({ payLater: false })}
            isPending={isBusy}
          />
        </>
      )}
    </div>
  )
}
